import { useEffect, useRef } from 'react'
import { useVideosContext } from '../context/videos-context'
import {
  getGenAiProjectDataApi,
  renderGenAiVideoApi,
  getGenAiVideoUrlsApi
} from '../context/api-helpers'
import {
  IAiFlowGenerationSocketData,
  IAiFlowRenderSocketData
} from './interfaces'
import { automaticallyDownloadVideo } from './helpers'
import { AI_VIDEO_GENERATION_STATES, VIDEO_RENDER_STATES } from './consts'
import { getEnvs } from 'utils/envs'
const { isProd } = getEnvs()

export const useExportSocket = () => {
  const { dispatch } = useVideosContext()
  const renderedVideosRef = useRef<Set<number>>(new Set())
  const fetchedVideoDataRef = useRef<Set<number>>(new Set())

  const handleGenAiVideoGeneration = async (
    data: IAiFlowGenerationSocketData
  ) => {
    const genAiVideoHash = data.gen_ai_video_hash
    if (!genAiVideoHash) return

    if (
      data.state === AI_VIDEO_GENERATION_STATES.preparing_media &&
      data.percent
    ) {
      dispatch({
        type: 'UPDATE_VIDEO_BY_HASH',
        payload: {
          genAiVideoHash,
          updates: {
            generationState: 'preparing_media',
            generationProgress: data.percent
          }
        }
      })
    }

    if (data.state === AI_VIDEO_GENERATION_STATES.finished && data.videoId) {
      dispatch({
        type: 'UPDATE_VIDEO_BY_HASH',
        payload: {
          genAiVideoHash,
          updates: {
            generationState: 'preparing_render',
            generationProgress: 100,
            videoId: data.videoId,
            renderState: 'idle'
          }
        }
      })

      // Only render if we haven't already initiated render for this videoId
      if (!renderedVideosRef.current.has(data.videoId)) {
        renderedVideosRef.current.add(data.videoId)
        await renderGenAiVideoApi(data.videoId)
      }
    } else if (data.state === AI_VIDEO_GENERATION_STATES.error) {
      dispatch({
        type: 'UPDATE_VIDEO_BY_HASH',
        payload: {
          genAiVideoHash,
          updates: {
            generationState: 'error',
            generationError: data.error || data.message || 'Unknown error'
          }
        }
      })
    }
  }

  const handleGenAiVideoRender = async (data: IAiFlowRenderSocketData) => {
    //data.projectId is the id of the video project in our data it videoId
    // for understnad wich video is rendering you can compare projectId with videoId
    const videoId = data.projectId
    if (!videoId) return

    if (data.status === VIDEO_RENDER_STATES.error) {
      dispatch({
        type: 'UPDATE_VIDEO_BY_VIDEO_ID',
        payload: {
          videoId,
          updates: {
            renderState: 'error',
            renderError: 'Rendering failed'
          }
        }
      })
    }

    if (data.state === VIDEO_RENDER_STATES.preparing) {
      dispatch({
        type: 'UPDATE_VIDEO_BY_VIDEO_ID',
        payload: {
          videoId,
          updates: {
            generationState: undefined,
            renderState: 'preparing',
            renderProgress: 0
          }
        }
      })
    }

    if (
      data.state === VIDEO_RENDER_STATES.rending &&
      data.percent !== undefined
    ) {
      dispatch({
        type: 'UPDATE_VIDEO_BY_VIDEO_ID',
        payload: {
          videoId,
          updates: {
            generationState: undefined,
            renderState: 'rendering',
            renderProgress: data.percent
          }
        }
      })
    }

    if (
      data.state === VIDEO_RENDER_STATES.uploading &&
      data.percent !== undefined
    ) {
      dispatch({
        type: 'UPDATE_VIDEO_BY_VIDEO_ID',
        payload: {
          videoId,
          updates: {
            generationState: undefined,
            renderState: 'uploading',
            renderProgress: data.percent
          }
        }
      })
    }

    if (data.state === VIDEO_RENDER_STATES.rended) {
      dispatch({
        type: 'UPDATE_VIDEO_BY_VIDEO_ID',
        payload: {
          videoId,
          updates: {
            generationState: 'finished',
            renderState: 'completed',
            renderProgress: 100
          }
        }
      })

      // Only fetch video data if we haven't already fetched it for this videoId
      if (!fetchedVideoDataRef.current.has(videoId)) {
        fetchedVideoDataRef.current.add(videoId)

        const projectData = await getGenAiProjectDataApi(videoId)
        const videoData = await getGenAiVideoUrlsApi(projectData.data.uid)

        automaticallyDownloadVideo(
          videoData.data.name,
          videoData.data.exports[0].url,
          isProd ? 'prod' : 'local'
        )
      }
    }
  }

  useEffect(() => {
    window.updateProgressBar = data => {
      if ('gen_ai_video_hash' in data) {
        handleGenAiVideoGeneration(data)
      } else if (data.type === 'video_project') {
        handleGenAiVideoRender(data)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
}
